import { FormProps } from 'types/form';
import { NestedProduct } from '../types/dtos';
import { ResponsePlaceDto } from 'features/place/types/dtos';
import { ResponseNestedProductCategoryDto } from 'features/nested-product-category/types/dtos';
import { ResponseCompanyDto } from 'features/company/types/dtos';
import {
    Autocomplete,
    Button,
    Checkbox,
    FormControlLabel,
    IconButton,
    SelectChangeEvent,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { PlaceConfigData } from 'types/dtos';
import React, { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import OWForm from 'components/layout/ow-form';
import { ToolTip } from 'components/controls';
import { InfoOutlined } from '@mui/icons-material';
import OWSelect from 'components/design/ow-select';
import { useFilterSelectOptions } from '../constants/status';
import FormFooter from 'components/layout/form-footer';
import { LoadingButton } from '@mui/lab';
import { isEmpty } from 'utils/object-helpers';
import { MultiSelect } from 'components/design/multi-select';

export type NestedProductFormProps = FormProps<NestedProduct> & {
    places?: { content: ResponsePlaceDto[] };
    nestedProductCategories?: { content: ResponseNestedProductCategoryDto[] };
    companies?: { content: ResponseCompanyDto[] };
    isAdmin?: boolean;
    isEdit?: boolean;
    websiteId?: string;
    placeId: string;
    individualPrices?: boolean;
    onSelectConfigChange?: (event: SelectChangeEvent<string[]>) => void;
    setIndividualPrices?: (value: boolean) => void;
    placeConfigData: PlaceConfigData[];
    onConfigPriceChange: (field: string, value: string, placeId?: string) => void;
    updateAll?: boolean;
    setUpdateAll?: (checked: boolean) => void;
};

type NestedProductFormType = (props: NestedProductFormProps) => ReactNode;

export const NestedProductForm: NestedProductFormType = ({
    formData: product,
    places,
    nestedProductCategories,
    onChange,
    onSubmit,
    onCancel,
    isSaveLoading,
    errors,
    isEdit,
    individualPrices,
    onSelectConfigChange,
    setIndividualPrices,
    placeConfigData,
    onConfigPriceChange,
    placeId,
    updateAll,
    setUpdateAll,
    disableSubmit,
}) => {
    const { t } = useTranslation();
    const FILTER_SELECT_OPTIONS = useFilterSelectOptions();

    return (
        <OWForm onSubmit={onSubmit}>
            <Typography variant="h6">{t('NESTED_PRODUCT_FORM.HEADER')}</Typography>
            <Stack direction="row" spacing={1} alignItems="flex-start">
                <Stack width="100%" spacing={1.5}>
                    <TextField
                        value={product.name}
                        onChange={(e) => onChange('name', e.target.value)}
                        placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_NAME')}
                        label={t('NESTED_PRODUCT_FORM.LABEL_NAME')}
                        required
                        autoFocus
                        error={!!errors?.name}
                        helperText={errors?.name?.errorMessage}
                    />
                    <Autocomplete
                        options={nestedProductCategories?.content || []}
                        value={
                            nestedProductCategories?.content.find(
                                (category) => category.id === product.nestedProductCategoryId,
                            ) || null
                        }
                        getOptionLabel={(option: { id: string; name: string }) => option.name}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={t('NESTED_PRODUCT_FORM.LABEL_PRODUCT_CATECORY')}
                                required
                                error={!!errors?.nestedProductCategoryId}
                                helperText={errors?.nestedProductCategoryId?.errorMessage}
                            />
                        )}
                        onChange={(_, value) => onChange('nestedProductCategoryId', value?.id)}
                        disabled={isEdit}
                    />
                </Stack>
            </Stack>
            <MultiSelect
                items={places?.content}
                selectedIds={placeConfigData.map((config) => config.placeId)}
                onSelectChange={onSelectConfigChange ? onSelectConfigChange : () => {}}
                translationKey="NESTED_PRODUCT_FORM.LABEL_PLACE"
                disabled={individualPrices || isEdit}
                required
            />
            <TextField
                value={product.description || ''}
                onChange={(e) => onChange('description', e.target.value)}
                placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_DESCRIPTION')}
                label={t('NESTED_PRODUCT_FORM.LABEL_DESCRIPTION')}
                type="text"
                error={!!errors?.description}
                helperText={errors?.description?.errorMessage}
                multiline
                minRows={3}
            />
            {!individualPrices && (
                <React.Fragment>
                    <Stack direction="row" spacing={1}>
                        <TextField
                            value={placeConfigData[0]?.price || ''}
                            onChange={(e) => onConfigPriceChange('price', e.target.value)}
                            label={t('NESTED_PRODUCT_FORM.LABEL_PRICE')}
                            placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_PRICE')}
                            fullWidth
                            error={!!errors?.[`price-${placeId}`]}
                            helperText={errors?.[`price-${placeId}`]?.errorMessage}
                            disabled={individualPrices}
                        />
                        <Stack direction={'row'} spacing={1} width="100%">
                            <TextField
                                value={placeConfigData[0]?.discountedPrice || ''}
                                onChange={(e) => onConfigPriceChange('discountedPrice', e.target.value)}
                                label={t('NESTED_PRODUCT_FORM.LABEL_DISCOUNTED_PRICE')}
                                placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_DISCOUNTED_PRICE')}
                                fullWidth
                                error={!!errors?.[`discountedPrice-${placeId}`]}
                                helperText={errors?.[`discountedPrice-${placeId}`]?.errorMessage}
                                disabled={!placeConfigData[0]?.price || individualPrices}
                                sx={{ width: '100%' }}
                            />
                            <ToolTip title={t('PRODUCT_FORM.DISCOUNTED_PRICE_INFO')}>
                                <IconButton sx={{ alignSelf: 'flex-start' }}>
                                    <InfoOutlined />
                                </IconButton>
                            </ToolTip>
                        </Stack>
                    </Stack>
                    <OWSelect
                        value={placeConfigData[0]?.isAvailable.toString() || 'AVAILABLE'}
                        items={FILTER_SELECT_OPTIONS}
                        label={t('NESTED_PRODUCT_FORM.LABEL_STATUS')}
                        labelId="label-status"
                        onChange={(e) => onConfigPriceChange('isAvailable', e.target.value)}
                        disabled={individualPrices}
                    />
                </React.Fragment>
            )}
            {isEdit ? (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={updateAll}
                            onChange={(e) => {
                                setUpdateAll && setUpdateAll(e.target.checked);
                            }}
                            color="primary"
                        />
                    }
                    label={t('NESTED_PRODUCT_FORM.UPDATE_ALL_CONFIGS')}
                />
            ) : (
                <FormControlLabel
                    control={
                        <Checkbox
                            checked={individualPrices}
                            onChange={(e) => {
                                setIndividualPrices && setIndividualPrices(e.target.checked);
                            }}
                            color="primary"
                            disabled={placeConfigData.length <= 1}
                        />
                    }
                    label={t('NESTED_PRODUCT_FORM.INDIVIDUAL_PRICES')}
                />
            )}

            {individualPrices && placeConfigData.length > 1 && (
                <React.Fragment>
                    {placeConfigData.map((config) => (
                        <Stack key={config.placeId} direction="column" spacing={1}>
                            <Typography>{config.placeName}</Typography>
                            <Stack direction="row" spacing={1}>
                                <TextField
                                    value={config.price.toString() || ''}
                                    onChange={(e) => onConfigPriceChange('price', e.target.value, config.placeId)}
                                    label={t('NESTED_PRODUCT_FORM.LABEL_PRICE')}
                                    placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_PRICE')}
                                    required
                                    fullWidth
                                    error={!!errors?.[`price-${config.placeId}`]}
                                    helperText={errors?.[`price-${config.placeId}`]?.errorMessage}
                                />
                                <Stack direction={'row'} spacing={1} width="100%">
                                    <TextField
                                        value={config.discountedPrice?.toString() || ''}
                                        onChange={(e) =>
                                            onConfigPriceChange('discountedPrice', e.target.value, config.placeId)
                                        }
                                        label={t('NESTED_PRODUCT_FORM.LABEL_DISCOUNTED_PRICE')}
                                        placeholder={t('NESTED_PRODUCT_FORM.PLACEHOLDER_DISCOUNTED_PRICE')}
                                        fullWidth
                                        error={!!errors?.[`discountedPrice-${config.placeId}`]}
                                        helperText={errors?.[`discountedPrice-${config.placeId}`]?.errorMessage}
                                        disabled={!config.price}
                                        sx={{ width: '100%' }}
                                    />
                                </Stack>
                            </Stack>
                            <OWSelect
                                value={config.isAvailable.toString() || ''}
                                items={FILTER_SELECT_OPTIONS}
                                label={t('NESTED_PRODUCT_FORM.LABEL_STATUS')}
                                labelId="label-status"
                                onChange={(e) => onConfigPriceChange('isAvailable', e.target.value, config.placeId)}
                            />
                        </Stack>
                    ))}
                </React.Fragment>
            )}
            <FormFooter>
                <Stack direction="row" width="360px" spacing={3}>
                    <Button variant="outlined" onClick={onCancel}>
                        {t('NESTED_PRODUCT_FORM.CANCEL_BUTTON')}
                    </Button>
                    <LoadingButton loading={isSaveLoading} type="submit" disabled={!isEmpty(errors) || disableSubmit}>
                        {t('NESTED_PRODUCT_FORM.SAVE_BUTTON')}
                    </LoadingButton>
                </Stack>
            </FormFooter>
        </OWForm>
    );
};
